import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { CalendarToday, Help } from "@material-ui/icons";
import InputCustom from "./InputCustom";
import Button from "./Button";
import TooltipCVV from "./TooltipCVV";
import CheckboxCustom from "./CheckboxCustom";
import {
  formatCNPJInput,
  formatNumeroCartao,
  formatDataCartaoInput,
  formatCVV,
} from "../utils/Utils";
import styles from "../styles/ModalAdicionarCartao.module.scss";

/**
 * Modal padronizado para adicionar um novo cartão de crédito.
 *
 * @param {boolean} aberto - Controla a visibilidade do modal
 * @param {Function} onFechar - Callback para fechar o modal
 * @param {Object} form_cartao - Estado do formulário do cartão
 * @param {Object} erros_cartao - Erros de validação do formulário
 * @param {Object} touched - Campos que foram tocados
 * @param {boolean} loading - Indica se há uma requisição em andamento
 * @param {Function} onChangeCartao - Callback chamado ao alterar um campo (campo, valor)
 * @param {Function} onSalvar - Callback chamado ao submeter o formulário
 */
export default function ModalAdicionarCartao({
  aberto,
  onFechar,
  form_cartao,
  erros_cartao,
  touched,
  loading,
  onChangeCartao,
  onSalvar,
}) {
  const [visivel, setVisivel] = useState(aberto);
  const [saindo, setSaindo] = useState(false);

  useEffect(() => {
    if (aberto) {
      setVisivel(true);
      setSaindo(false);
    } else if (visivel) {
      setSaindo(true);

      const timer = setTimeout(() => {
        setVisivel(false);
        setSaindo(false);
      }, 220);

      return () => clearTimeout(timer);
    }
  }, [aberto, visivel]);

  if (!visivel) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={`${styles.overlay} ${saindo ? styles.saindo : ""}`} onClick={onFechar}>
      <div className={`${styles.modal} ${saindo ? styles.modalSaindo : ""}`} onClick={(evento) => evento.stopPropagation()}>
        <div className={styles.modalHeader}>
          <p className={styles.modalTitulo}>Adicionar cartão</p>
          <button className={styles.modalFechar} onClick={onFechar}>×</button>
        </div>

        <div className={styles.modalBody}>
          <InputCustom
            label="Nome do titular"
            name="nome_titular"
            type="text"
            value={form_cartao.nome_titular}
            onChange={(evento) => onChangeCartao("nome_titular", evento.target.value)}
            disabled={loading}
            required
            errors={erros_cartao}
            touched={touched}
          />

          <InputCustom
            label="CNPJ do titular"
            name="cnpj_titular"
            type="text"
            value={form_cartao.cnpj_titular}
            onChange={(evento) => onChangeCartao("cnpj_titular", formatCNPJInput(evento).target.value)}
            maxLength={18}
            disabled={loading}
            required
            errors={erros_cartao}
            touched={touched}
          />

          <InputCustom
            label="Número do cartão"
            name="numero_cartao"
            type="text"
            value={form_cartao.numero_cartao}
            onChange={(evento) => onChangeCartao("numero_cartao", formatNumeroCartao(evento).target.value)}
            disabled={loading}
            required
            errors={erros_cartao}
            touched={touched}
          />

          <div className={styles.gridDuasColunas}>
            <InputCustom
              label="Validade"
              name="validade"
              type="text"
              value={form_cartao.validade}
              onChange={(evento) => onChangeCartao("validade", formatDataCartaoInput(evento).target.value)}
              disabled={loading}
              required
              errors={erros_cartao}
              touched={touched}
              icon_left={<CalendarToday className="color-main-10" />}
            />

            <InputCustom
              label="CVV"
              name="cvv"
              type="text"
              value={form_cartao.cvv}
              onChange={(evento) => onChangeCartao("cvv", formatCVV(evento).target.value)}
              maxLength={4}
              disabled={loading}
              required
              errors={erros_cartao}
              touched={touched}
              icon_left={<Help className="color-gray-6" />}
              tooltip_custom={<TooltipCVV />}
            />
          </div>

          <CheckboxCustom
            label="Forma de pagamento principal"
            checked={form_cartao.pagamento_principal}
            onChange={(evento) => onChangeCartao("pagamento_principal", evento.target.checked)}
            disabled={loading}
          />
        </div>

        <Button
          type="button"
          variant="primary"
          size="lg"
          fullWidth
          loading={loading}
          disabled={loading}
          onClick={onSalvar}
        >
          Salvar dados do cartão
        </Button>
      </div>
    </div>,
    document.body
  );
}
